import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, FileText, CheckCircle, Clock, AlertCircle, 
  Building2, TrendingUp, CreditCard, Download, ExternalLink,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '@/components/DashboardLayout';
import { DocumentSigningModal, InvestmentJourney } from '@/components/investor/DocumentSigning';
import { formatINR } from '@/utils/currency';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvestmentDetail({ user }) {
  const navigate = useNavigate();
  const { investmentId } = useParams();
  const [investment, setInvestment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchInvestment();
  }, [investmentId]);

  const fetchInvestment = async () => {
    if (!investmentId) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/investments/${investmentId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setInvestment(data.investment || data);
      } else {
        toast.error('Failed to load investment details');
      }
    } catch (error) {
      console.error('Error fetching investment:', error);
      toast.error('Failed to load investment details');
    } finally {
      setLoading(false);
    }
  };

  const handleSendDocument = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/v2/investments/${investmentId}/send-agreement?use_flow=true`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Document sent for signature!');
        fetchInvestment(); // Refresh to get updated state
      } else {
        toast.error(data.detail || 'Failed to send document');
      }
    } catch (error) {
      console.error('Error sending document:', error);
      toast.error('Failed to send document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSigningComplete = () => {
    toast.success('Document signed successfully!');
    setShowSigningModal(false);
    fetchInvestment(); // Refresh state
  };

  const getStateLabel = (state) => {
    const labels = {
      'EOI_PENDING': 'EOI Pending',
      'EOI_PAID': 'EOI Paid - Awaiting Document',
      'DOC_SENT': 'Document Ready for Signature',
      'DOC_SIGNED': 'Document Signed',
      'CAPITAL_PENDING': 'Capital Payment Pending',
      'CAPITAL_PAID': 'Capital Paid',
      'ACTIVE': 'Active Investment',
      'COMPLETED': 'Completed'
    };
    return labels[state] || state;
  };

  const getStateColor = (state) => {
    const colors = {
      'EOI_PENDING': 'text-yellow-500',
      'EOI_PAID': 'text-blue-500',
      'DOC_SENT': 'text-orange-500',
      'DOC_SIGNED': 'text-green-500',
      'CAPITAL_PENDING': 'text-yellow-500',
      'CAPITAL_PAID': 'text-green-500',
      'ACTIVE': 'text-emerald-500',
      'COMPLETED': 'text-gray-500'
    };
    return colors[state] || 'text-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <DashboardLayout user={user}>
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
          </div>
        </DashboardLayout>
      </div>
    );
  }

  if (!investment) {
    return (
      <div className="min-h-screen bg-black">
        <DashboardLayout user={user}>
          <div className="p-6 md:p-10">
            <div className="text-center py-20">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">Investment Not Found</h3>
              <button
                onClick={() => navigate('/investor/portfolio')}
                className="mt-4 px-6 py-2 bg-[#4ADE80] text-black rounded"
              >
                Back to Portfolio
              </button>
            </div>
          </div>
        </DashboardLayout>
      </div>
    );
  }

  const state = investment.state || investment.status;
  const amounts = investment.amounts || {};
  const document = investment.document || {};

  return (
    <div className="min-h-screen bg-black">
      <DashboardLayout user={user}>
        <div className="p-6 md:p-10">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => navigate('/investor/portfolio')}
              className="p-2 hover:bg-white/10 rounded transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <p className="text-xs text-gray-600 uppercase tracking-widest mb-1">Investment Details</p>
              <h1 className="text-2xl font-light text-white">
                {investment.unit?.projectName || investment.projectName || 'Investment'}
              </h1>
            </div>
          </div>

          {/* Investment Journey Progress */}
          <div className="mb-8">
            <InvestmentJourney 
              investment={investment} 
              onSignDocument={() => setShowSigningModal(true)}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Investment Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Status Card */}
              <div className="bg-white/5 border border-white/10 p-6" data-testid="investment-status-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-white uppercase tracking-wider">Current Status</h3>
                  <span className={`px-3 py-1 text-xs font-medium rounded ${getStateColor(state)} bg-white/10`}>
                    {getStateLabel(state)}
                  </span>
                </div>
                
                {/* Action based on state */}
                {state === 'EOI_PAID' && (
                  <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Document Preparation</p>
                        <p className="text-sm text-gray-400">Your LLP Agreement is being prepared</p>
                      </div>
                      <button
                        onClick={handleSendDocument}
                        disabled={actionLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                        data-testid="send-document-btn"
                      >
                        {actionLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <FileText className="w-4 h-4" />
                        )}
                        Send Document
                      </button>
                    </div>
                  </div>
                )}

                {state === 'DOC_SENT' && (
                  <div className="mt-4 p-4 bg-orange-500/10 border border-orange-500/20 rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Document Ready for Signature</p>
                        <p className="text-sm text-gray-400">Please review and sign your LLP Agreement</p>
                      </div>
                      <button
                        onClick={() => setShowSigningModal(true)}
                        className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center gap-2"
                        data-testid="sign-document-btn"
                      >
                        <FileText className="w-4 h-4" />
                        Sign Now
                      </button>
                    </div>
                  </div>
                )}

                {state === 'DOC_SIGNED' && (
                  <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                      <div>
                        <p className="text-white font-medium">Document Signed</p>
                        <p className="text-sm text-gray-400">
                          Signed on {document.signed_at ? new Date(document.signed_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Financial Details */}
              <div className="bg-white/5 border border-white/10 p-6">
                <h3 className="text-sm font-medium text-white uppercase tracking-wider mb-4">Financial Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Total Commitment</p>
                    <p className="text-xl font-mono text-white">
                      {formatINR(amounts.totalCommitment || investment.amount || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">EOI Amount</p>
                    <p className="text-xl font-mono text-[#4ADE80]">
                      {formatINR(amounts.eoiAmount || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Capital Amount</p>
                    <p className="text-xl font-mono text-white">
                      {formatINR(amounts.capitalAmount || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Ownership</p>
                    <p className="text-xl font-mono text-[#4ADE80]">
                      {investment.ownershipPercentage || '0'}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Document History */}
              {document.zoho_request_id && (
                <div className="bg-white/5 border border-white/10 p-6">
                  <h3 className="text-sm font-medium text-white uppercase tracking-wider mb-4">Document</h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-blue-400" />
                      <div>
                        <p className="text-white">{document.document_name || 'LLP Agreement'}</p>
                        <p className="text-xs text-gray-500">
                          Sent: {document.sent_at ? new Date(document.sent_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${
                      document.status === 'SIGNED' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {document.status || 'Pending'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Property Info Sidebar */}
            <div className="space-y-6">
              <div className="bg-white/5 border border-white/10 p-6">
                <h3 className="text-sm font-medium text-white uppercase tracking-wider mb-4">Property</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-white">{investment.unit?.unitName || investment.unitId}</p>
                      <p className="text-xs text-gray-500">{investment.unit?.location || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* LLP Info */}
              {investment.llp && (
                <div className="bg-white/5 border border-white/10 p-6">
                  <h3 className="text-sm font-medium text-white uppercase tracking-wider mb-4">LLP Details</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-white">{investment.llp.name}</p>
                    <p className="text-gray-500">LLPIN: {investment.llp.llpin}</p>
                  </div>
                </div>
              )}

              {/* Help */}
              <div className="bg-white/5 border border-white/10 p-6">
                <h3 className="text-sm font-medium text-white uppercase tracking-wider mb-4">Need Help?</h3>
                <p className="text-sm text-gray-400 mb-4">
                  Contact our support team for any questions about your investment.
                </p>
                <a 
                  href="mailto:support@altslice.com"
                  className="text-[#4ADE80] text-sm hover:underline"
                >
                  support@altslice.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>

      {/* Document Signing Modal */}
      <DocumentSigningModal
        isOpen={showSigningModal}
        onClose={() => setShowSigningModal(false)}
        investmentId={investmentId}
        onSigningComplete={handleSigningComplete}
      />
    </div>
  );
}
